import { useState, useEffect, MutableRefObject } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Sketch from "@arcgis/core/widgets/Sketch";
import { getSymbolForGeometryType, getHoverSymbolForGeometryType } from "../../utils/arcgisHelper";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import { getArcGISGeometryType } from "../../utils/arcgisHelper";
import { getMapFeatures } from "../../queries/maps";
import { graphicReadonlyIndex } from "../../components/FeatureSelectorModal";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";

interface UseReadonlySketchEventsOptions {
  readonlyLayerRef: MutableRefObject<GraphicsLayer | null>;
  readonlySketchRef: MutableRefObject<Sketch | null>;
  readonlySketchInitialized: boolean;
  selectedFeatures: { [key: string]: boolean };
  setSelectedFeatures: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
}

interface LayerFeature {
  id: number;
  name: string;
  geometry: Record<string, any>;
  attributes?: Record<string, any>;
}

export function useReadonlySketchEvents({
  readonlyLayerRef,
  readonlySketchRef,
  readonlySketchInitialized,
  selectedFeatures,
  setSelectedFeatures,
}: UseReadonlySketchEventsOptions) {
  const [loadedLayers, setLoadedLayers] = useState<string[]>([]);
  const [layerFeaturesMap, setLayerFeaturesMap] = useState<{ [layerId: string]: LayerFeature[] }>({});
  const [sketchHandler, setSketchHandler] = useState<{ remove: () => void } | null>(null);
  const networkHeaders = useNetworkHeaders();

  const loadLayerFeatures = async (layerId: string, preloadedFeatures?: LayerFeature[]) => {
    if (loadedLayers.includes(layerId) && preloadedFeatures) {
      return updateLayerFeatures(layerId, preloadedFeatures);
    }

    try {
      let formattedFeatures: LayerFeature[];

      if (preloadedFeatures) {
        formattedFeatures = preloadedFeatures;
      } else {
        const features = await getMapFeatures(networkHeaders)(layerId);

        // Format the features
        formattedFeatures = features
          .map((feature) => {
            // Find the primary field to use as the name
            let primaryFieldValue = "My Feature";
            if (feature.customFields && feature.customFields.length > 0) {
              const primaryField = feature.customFields.find((field) => field.isPrimary);
              if (primaryField) {
                primaryFieldValue = primaryField.value || primaryFieldValue;
              } else {
                // Use the first field as a fallback
                primaryFieldValue = feature.customFields[0].value || primaryFieldValue;
              }
            }

            // Check if the feature has a polygon geometry
            const geometryType = feature.geometry?.type;
            if (geometryType !== "Polygon" && geometryType !== "MultiPolygon") return null;

            return {
              id: feature.id,
              name: primaryFieldValue,
              geometry: feature.geometry,
              attributes:
                feature.customFields?.reduce(
                  (acc, field) => {
                    acc[field.id] = field.value;
                    return acc;
                  },
                  {} as Record<string, any>,
                ) ?? {},
            };
          })
          .filter(Boolean) as LayerFeature[];
      }

      setLayerFeaturesMap((prev) => ({
        ...prev,
        [layerId]: formattedFeatures,
      }));
      setLoadedLayers((prev) => [...prev, layerId]);
      addFeaturesToGraphicsLayer(layerId, formattedFeatures);
    } catch (error) {
      console.error(`Error loading features for layer ${layerId}:`, error);
    }
  };

  const addFeaturesToGraphicsLayer = (layerId: string, features: LayerFeature[]) => {
    if (!readonlyLayerRef.current) return;

    // Clear any existing index entries for this layer
    if (graphicReadonlyIndex) {
      const currentGraphics = readonlyLayerRef.current.graphics.toArray();
      currentGraphics.forEach((graphic) => {
        if (graphic.attributes?.layerId === layerId && graphic.attributes?.featureId) {
          graphicReadonlyIndex.delete(graphic.attributes.featureId.toString());
        }
      });
    }

    // Remove existing graphics for this layer
    const graphicsToRemove = readonlyLayerRef.current.graphics.toArray().filter((graphic) => {
      return graphic.attributes?.layerId === layerId;
    });
    if (graphicsToRemove.length) {
      readonlyLayerRef.current.graphics.removeMany(graphicsToRemove);
    }

    // Add new graphics
    const graphics = features.map((feature) => {
      return new Graphic({
        geometry: {
          ...geojsonToArcGIS(feature.geometry as GeoJSON.GeoJSON),
          type: getArcGISGeometryType(feature.geometry.type),
        },
        attributes: {
          ...(feature.attributes || {}),
          featureId: feature.id,
          layerId,
          name: feature.name,
        },
        symbol: getSymbolForGeometryType(feature.geometry.type) as any,
      });
    });

    readonlyLayerRef.current.graphics.addMany(graphics);

    // Update index
    const allGraphics = readonlyLayerRef.current.graphics.toArray();
    allGraphics.forEach((graphic, index) => {
      if (graphic.attributes?.featureId && graphic.attributes?.layerId === layerId) {
        graphicReadonlyIndex.set(graphic.attributes.featureId.toString(), index);
      }
    });
  };

  // Update existing layer features (like with search results)
  const updateLayerFeatures = (layerId: string, features: LayerFeature[]) => {
    // Update the layer features map
    setLayerFeaturesMap((prev) => ({
      ...prev,
      [layerId]: features,
    }));

    // Update the graphics layer
    addFeaturesToGraphicsLayer(layerId, features);
  };

  const unloadLayerFeatures = async (layerId: string) => {
    if (!readonlyLayerRef.current) return;
    if (!loadedLayers.includes(layerId)) {
      return; // Features not loaded
    }

    try {
      // Remove the layer features from the map
      const graphicsToRemove = readonlyLayerRef.current.graphics.toArray().filter((graphic) => {
        return graphic.attributes.layerId === layerId;
      });

      if (graphicsToRemove.length) {
        graphicsToRemove.forEach((graphic) => {
          if (graphic.attributes?.featureId) {
            graphicReadonlyIndex.delete(graphic.attributes.featureId.toString());
          }
        });

        readonlyLayerRef.current.graphics.removeMany(graphicsToRemove);
      }

      // Remove the layer from the loaded layers list
      setLoadedLayers((prev) => prev.filter((id) => id !== layerId));

      // Remove the layer features from the layer features map
      setLayerFeaturesMap((prev) => {
        const newState = { ...prev };
        delete newState[layerId];
        return newState;
      });

      if (readonlyLayerRef.current) {
        const remainingGraphics = readonlyLayerRef.current.graphics.toArray();
        remainingGraphics.forEach((graphic, index) => {
          if (graphic.attributes?.featureId) {
            graphicReadonlyIndex.set(graphic.attributes.featureId.toString(), index);
          }
        });
      }
    } catch (error) {
      console.error(`Error unloading features for layer ${layerId}:`, error);
    }
  };

  const toggleFeatureSelection = (featureId: string, state?: boolean) => {
    setSelectedFeatures((prev) => {
      const newState = { ...prev };
      newState[featureId] = state ?? !newState[featureId];
      return newState;
    });

    // Update feature highlight on the map
    if (!readonlyLayerRef.current || !graphicReadonlyIndex.has(featureId)) return;
    const feature = readonlyLayerRef.current.graphics.at(graphicReadonlyIndex.get(featureId)!);
    if (!feature?.geometry) return;
    feature.symbol =
      state === false || (state !== true && !selectedFeatures[featureId])
        ? getSymbolForGeometryType(feature.geometry.type)
        : getHoverSymbolForGeometryType(feature.geometry.type);
  };

  const readdHandler = () => {
    if (readonlySketchRef.current == null) return null;
    sketchHandler?.remove();
    const handler = readonlySketchRef.current.on("update", handleSketchUpdateSelection);
    setSketchHandler(handler);
    return handler;
  };
  useEffect(() => {
    const handler = readdHandler();
    return () => handler?.remove();
  }, [readonlySketchInitialized, selectedFeatures, graphicReadonlyIndex]);

  const handleSketchUpdateSelection: __esri.SketchUpdateEventHandler = async (event) => {
    switch (event.state) {
      case "start":
        // Immediately cancel the operation to prevent editing
        readonlySketchRef.current?.cancel();
        if (!event.graphics.length) break;
        const graphic = event.graphics[0];
        const featureId = graphic.attributes?.featureId?.toString();
        if (!featureId) break;
        setSelectedFeatures((prev) => {
          const newState = { ...prev };
          newState[featureId] = !newState[featureId];
          const graphics = readonlyLayerRef.current?.graphics;
          // When an item is selected, it gets reordered to the end of the graphics array
          const graphicToUpdate = graphics?.getItemAt(graphics.length - 1);
          if (graphicToUpdate?.geometry) {
            graphicToUpdate.symbol = newState[featureId]
              ? getHoverSymbolForGeometryType(graphicToUpdate.geometry.type)
              : getSymbolForGeometryType(graphicToUpdate.geometry.type);
          }
          return newState;
        });
        break;
    }
  };

  return {
    loadLayerFeatures,
    unloadLayerFeatures,
    updateLayerFeatures,
    layerFeaturesMap,
    toggleFeatureSelection,
  };
}
